import {TopNav} from "../../components/TopNavigation";
import {
  AppLayout,
  Autosuggest,
  AutosuggestProps,
  BreadcrumbGroup,
  Button,
  Container,
  ContentLayout,
  Header, Link,
  SpaceBetween,
  Textarea,
  TextContent
} from "@cloudscape-design/components-themed";
import {Navigation} from "../../components/Navigation";
import {useCallback, useState} from "react";
import {debounce} from "lodash";
import {NextPageContext} from "next";
import {useRouter} from "next/router";

interface Processor<T, U> {
  from: string,
  to: string,
  forward: (input: T) => U;
  backward: (input: U) => T;
}

const stringToJson: Processor<string, any> = {
  from: "text",
  to: "json",
  forward: (input: string) => JSON.parse(input),
  backward: (input: any) => JSON.stringify(input, undefined, 4),
}

const stringToBase64: Processor<string, string> = {
  from: "text",
  to: "base64",
  forward: (input: string) => Buffer.from(input, 'utf8').toString('base64'),
  backward: (input: string) => Buffer.from(input, "base64").toString('utf8'),
}

const stringToReverse: Processor<string, string> = {
  from: "text",
  to: "reverse",
  forward: (input: string) => input.split("").reverse().join(""),
  backward: (input: string) => input.split("").reverse().join(""),
}

const stringToUrlencoded: Processor<string, string> = {
  from: "string",
  to: "urlencoded",
  forward: (input: string) => encodeURIComponent(input),
  backward: (input: string) => decodeURIComponent(input),
}

enum CopyStatus {READY = "Copy", COPYING = "Copying", COPIED = "Copied"}

export function getServerSideProps(ctx: NextPageContext) {
  return {
    props: {}
  }
}

function Breadcrumbs() {
  const breadcrumbItems = [
    {
      text: 'Home',
      href: '/',
    },
    {
      text: 'Text Converter',
      href: '/convert',
    },
  ];
  return <BreadcrumbGroup items={breadcrumbItems} expandAriaLabel="Show path" ariaLabel="Breadcrumbs"/>;
}

export default function Page() {
  const router = useRouter();
  const [value, setValue] = useState("");
  const [result, setResult] = useState("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [from, setFrom] = useState(typeof router.query["from"] === "string" ? router.query["from"] : "text");
  const [to, setTo] = useState(typeof router.query["to"] === "string" ? router.query["to"] : "base64");

  const fromTypes: AutosuggestProps.Option[] = [
    "json",
    "text",
    "urlencoded",
    "base64",
    "reverse",
  ].map(value => ({value, __labelPrefix: ""}));
  const toTypes: AutosuggestProps.Option[] = [
    "json",
    "text",
    "urlencoded",
    "base64",
    "reverse",
  ].map(value => ({value, __labelPrefix: ""}));

  const onSwitchClicked = useCallback(() => {
    setFrom(to);
    setTo(from);
    setResult(value);
    setValue(result);
  }, [to, from, result, value]);

  const runProcess = useCallback((data: string) => {
    try {
      let intermediate = "";
      switch (from) {
        case "text":
          intermediate = data;
          break;
        case "json":
          intermediate = data;
          break;
        case "urlencoded":
          intermediate = stringToUrlencoded.backward(data);
          break;
        case "base64":
          intermediate = stringToBase64.backward(data);
          break;
        case "reverse":
          intermediate = stringToReverse.backward(data);
          break;
        default:
          break;
      }
      switch (to) {
        case "urlencoded":
          setResult(stringToUrlencoded.forward(data));
          break;
        case "base64":
          setResult(stringToBase64.forward(intermediate));
          break;
        case "json":
          setResult(stringToJson.backward(stringToJson.forward(intermediate)));
          break;
        case "reverse":
          setResult(stringToReverse.forward(intermediate));
          break;
        default:
          setResult(intermediate);
      }
      setError("");
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError(`not JSON: ${err.message}`);
      } else {
        setError("not base64");
      }
    } finally {
      setProcessing(false);
    }
  }, [from, to]);

  const setValueDebounce = debounce((data) => {
    runProcess(data);
  }, 500);

  const [copyStatus, setCopyStatus] = useState(CopyStatus.READY);
  const onCopyClicked = useCallback((text: string) => {
    setCopyStatus(CopyStatus.COPYING);
    navigator.clipboard.writeText(text);
    setTimeout(() => {
      setCopyStatus(CopyStatus.COPIED);
    }, 200);
    setTimeout(() => {
      setCopyStatus(CopyStatus.READY);
    }, 1000);
  }, []);

  return (
    <>
      <TopNav/>
      <AppLayout
        content={
          <ContentLayout
            header={
              <Header
                variant="h1"
                info={
                  <Link variant="info" ariaLabel={'Information about text converter.'}/>
                }
              >
                Text Converter
              </Header>
            }
          >
            <SpaceBetween size="m">
              <Header variant="h1">Text Converter</Header>

              <Container>
                <SpaceBetween size="m">
                  <SpaceBetween size="s">
                    <SpaceBetween size="s" direction="horizontal">
                      <Header variant="h2">Input</Header>
                      <Autosuggest
                        enteredTextLabel={value => `Use: "${value}"`}
                        value={from}
                        onChange={(e) => setFrom(e.detail.value)}
                        options={fromTypes}
                        placeholder="Enter value"
                        empty="No matches found"
                      />
                    </SpaceBetween>
                    <Textarea
                      value={value}
                      onChange={(event) => {
                        setProcessing(true);
                        setValue(event.detail.value);
                        setValueDebounce(event.detail.value)
                      }}
                    />
                  </SpaceBetween>
                  <SpaceBetween size="s" direction="horizontal">
                    <Button variant="primary" iconName="upload" onClick={_ => runProcess(value)}
                            loading={processing}>Convert</Button>
                    <Button variant="normal" iconName="undo" onClick={onSwitchClicked}>Switch</Button>
                    {error && <TextContent>{error}</TextContent>}
                  </SpaceBetween>
                  <SpaceBetween size="s">
                    <SpaceBetween size="s" direction="horizontal">
                      <Header variant="h2">Result</Header>
                      <Autosuggest
                        enteredTextLabel={value => `Use: "${value}"`}
                        value={to}
                        onChange={(e) => setTo(e.detail.value)}
                        options={toTypes}
                        placeholder="Enter value"
                        empty="No matches found"
                      />
                      <Button
                        iconName="copy"
                        onClick={() => onCopyClicked(result)}
                        loading={copyStatus == CopyStatus.COPYING}
                        disabled={copyStatus == CopyStatus.COPIED}
                      >
                        {copyStatus}
                      </Button>
                    </SpaceBetween>
                    <Textarea readOnly value={result}/>
                  </SpaceBetween>
                </SpaceBetween>
              </Container>
            </SpaceBetween>
          </ContentLayout>
        }
        headerSelector="#header"
        breadcrumbs={<Breadcrumbs/>}
        navigation={<Navigation/>}
        // tools={toolsContent}
        // toolsOpen={toolsOpen}
        // onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        // ariaLabels={appLayoutLabels}
        // notifications={<Notifications />}
      />
    </>

  )
}
